/**
 * Tutor preview for an interactive worksheet.
 *
 * The normal worksheet preview rasterises each page onto a canvas and wraps it in
 * Fabric for the pen tools, which turns form fields into pixels. This renders the
 * real AcroForm fields instead, so a tutor can see which boxes a student can
 * actually type in. A worksheet whose blanks were never authored as fields looks
 * identical to a working one until you try to click into them.
 *
 * Nothing typed here is saved. There are no pen tools on this path: an interactive
 * worksheet is answered through its fields.
 */
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import "react-pdf/dist/esm/Page/TextLayer.css"

// react-pdf 7.7.x bundles pdfjs 3.11.174, so the worker must be that same version.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const InteractiveWorksheetPreview = ({ pdfUrl, scale }) => {
  const [pdfData, setPdfData] = useState(null)
  const [numPages, setNumPages] = useState(0)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setPdfData(null)
    setNumPages(0)
    setError("")

    if (!pdfUrl) {
      setError("Worksheet not found")
      setLoading(false)
      return undefined
    }

    setLoading(true)
    const load = async () => {
      try {
        const res = await fetch(pdfUrl)
        if (!res.ok) throw new Error("Request failed with status " + res.status)
        const buf = new Uint8Array(await res.arrayBuffer())
        if (!cancelled) setPdfData(buf)
      } catch (err) {
        console.error("Failed to fetch interactive worksheet PDF", err)
        if (!cancelled) setError("This worksheet could not be loaded")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [pdfUrl])

  const file = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData])

  if (error) {
    return <p className="text-center p-4 text-danger fw-500">{error}</p>
  }

  return (
    <div className="d-flex flex-column align-items-center">
      {loading ? <p className="text-center p-4 text-dark fw-500">Loading…</p> : null}
      {file ? (
        <Document
          file={file}
          onLoadSuccess={pdf => setNumPages(pdf.numPages)}
          onLoadError={err => setError(err?.message || "This worksheet could not be loaded")}
          loading=""
        >
          {Array.from({ length: numPages }, (unused, i) => (
            <div key={i} className="mb-3 shadow-sm bg-white">
              <div className="d-flex justify-content-center">
                <p className="mb-1">{i + 1}/{numPages}</p>
              </div>
              <Page
                pageNumber={i + 1}
                renderForms
                renderAnnotationLayer
                renderTextLayer={false}
                scale={scale}
              />
            </div>
          ))}
        </Document>
      ) : null}
    </div>
  )
}

InteractiveWorksheetPreview.propTypes = {
  pdfUrl: PropTypes.string,
  scale: PropTypes.number,
}

InteractiveWorksheetPreview.defaultProps = {
  scale: 1.4,
}

export default InteractiveWorksheetPreview
